/**
 * Split View Types
 *
 * Type definitions for the global split view system using react-resizable-panels.
 * Supports unlimited nested panels with horizontal/vertical splits.
 */

// ============================================================================
// Panel Types
// ============================================================================

/**
 * Types of content that can be displayed in a split view panel.
 */
export type SplitPanelType =
  | "dashboard"
  | "tracker"
  | "trackers"
  | "alert"
  | "alerts"
  | "enrichment"
  | "enrichments"
  | "chat"
  | "search"
  | "home"
  | "account"
  | "widgets"
  | "branches"
  | "investigation"
  | "investigations"
  | "reports"
  | "workspaces"
  | "tokens"
  | "members"
  | "channels"
  | "organization"
  | "settings";

/**
 * A single panel in the split view.
 */
export interface SplitPanel {
  /** Unique identifier for this panel */
  id: string;
  /** Type of content displayed */
  type: SplitPanelType;
  /** Route path for this panel (e.g., "/dashboards?id=xxx") */
  route: string;
  /** Optional resource ID (dashboard ID, tracker ID, etc.) */
  resourceId?: string;
  /** Display title for the panel header */
  title?: string;
  /** Optional icon for the panel header */
  icon?: string;
  /** Optional color for the panel header */
  color?: string;
}

// ============================================================================
// Split Node Types (Tree Structure)
// ============================================================================

/**
 * Orientation for panel groups.
 */
export type SplitOrientation = "horizontal" | "vertical";

/**
 * A leaf node containing a single panel.
 */
export interface SplitPanelNode {
  type: "panel";
  panel: SplitPanel;
}

/**
 * A group node containing multiple children (panels or nested groups).
 */
export interface SplitGroupNode {
  type: "group";
  id: string;
  orientation: SplitOrientation;
  children: SplitNode[];
  /** Sizes as percentages for each child */
  sizes: number[];
}

/**
 * A node in the split view tree - either a panel or a group.
 */
export type SplitNode = SplitPanelNode | SplitGroupNode;

// ============================================================================
// Split View State
// ============================================================================

/**
 * State of the global split view system.
 */
export interface SplitViewState {
  /** Root node of the split tree (null when no panels) */
  root: SplitNode | null;
  /** ID of the currently focused panel */
  focusedPanelId: string | null;
}

// ============================================================================
// Split View Actions
// ============================================================================

/**
 * Actions available on the split view store.
 */
export interface SplitViewActions {
  /**
   * Check if split view is currently active (more than 1 panel).
   */
  isActive: () => boolean;

  /**
   * Get all panels as a flat array.
   */
  getPanels: () => SplitPanel[];

  /**
   * Add a new panel to the split view.
   * @param panel - Panel data (without id)
   * @param targetPanelId - If specified, splits this panel to add the new one
   * @param orientation - Direction to split (defaults to horizontal)
   * @param addBefore - If true, add new panel before target (left/top). Defaults to false (right/bottom)
   * @returns The new panel's ID
   */
  addPanel: (
    panel: Omit<SplitPanel, "id">,
    targetPanelId?: string,
    orientation?: SplitOrientation,
    addBefore?: boolean
  ) => string;

  /**
   * Remove a panel by ID.
   * If only one panel remains in a group, the group is simplified.
   */
  removePanel: (panelId: string) => void;

  /**
   * Set the focused panel by ID.
   */
  setFocusedPanelId: (panelId: string | null) => void;

  /**
   * Update sizes for a specific group.
   */
  updateGroupSizes: (groupId: string, sizes: number[]) => void;

  /**
   * Update a panel's properties.
   */
  updatePanel: (panelId: string, updates: Partial<Omit<SplitPanel, "id">>) => void;

  /**
   * Swap two panels by their IDs.
   */
  swapPanels: (panelIdA: string, panelIdB: string) => void;

  /**
   * Move a panel into another panel, creating a split.
   * @param sourcePanelId - Panel to move
   * @param targetPanelId - Panel to split
   * @param position - Where to place the source relative to target
   */
  movePanel: (
    sourcePanelId: string,
    targetPanelId: string,
    position: "left" | "right" | "top" | "bottom"
  ) => void;

  /**
   * Clear all panels and reset to initial state.
   */
  reset: () => void;

  /**
   * Initialize split view with a single panel.
   */
  initializeWithPanel: (panel: Omit<SplitPanel, "id">) => string;

  /**
   * Set the root node directly (used for loading page configurations).
   */
  setRoot: (root: SplitNode | null) => void;
}

// ============================================================================
// Store Type
// ============================================================================

/**
 * Combined store type with state and actions.
 */
export type SplitViewStore = SplitViewState & SplitViewActions;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Generate a unique ID for panels and groups.
 */
export function generateId(prefix: string = "split"): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Find a panel by ID in the tree.
 */
export function findPanelInTree(
  node: SplitNode | null,
  panelId: string
): SplitPanel | null {
  if (!node) return null;

  if (node.type === "panel") {
    return node.panel.id === panelId ? node.panel : null;
  }

  for (const child of node.children) {
    const found = findPanelInTree(child, panelId);
    if (found) return found;
  }

  return null;
}

/**
 * Cache for getAllPanels to avoid creating new arrays on every call.
 * Uses WeakMap so entries are garbage collected when root nodes are no longer referenced.
 */
const panelsCache = new WeakMap<SplitNode, SplitPanel[]>();
const emptyPanelsArray: SplitPanel[] = [];

/**
 * Get all panels from the tree as a flat array.
 * Results are cached based on the root node reference to avoid unnecessary array creation.
 */
export function getAllPanels(node: SplitNode | null): SplitPanel[] {
  if (!node) return emptyPanelsArray;

  // Check cache first
  const cached = panelsCache.get(node);
  if (cached) return cached;

  let result: SplitPanel[];
  if (node.type === "panel") {
    result = [node.panel];
  } else {
    result = node.children.flatMap((child) => getAllPanels(child));
  }

  // Cache the result
  panelsCache.set(node, result);
  return result;
}

/**
 * Count the total number of panels in the tree.
 */
export function countPanels(node: SplitNode | null): number {
  if (!node) return 0;

  if (node.type === "panel") {
    return 1;
  }

  return node.children.reduce((sum, child) => sum + countPanels(child), 0);
}
